/**
 * Fixtures for Food Order Triage Modal v2.
 * Reuses v1 question IDs where questions are same or relevant (03, 05b, 07, 08, 19, 20, 27, 28, 29, 34, 35, 37, 38, 50).
 * New IDs: 51, 52, 55a, 56b, 62, 63, 66a.
 */

/**
 * Canonical FHIR / survey question ids for v2. Change values here only when the questionnaire id changes.
 */
export const TRIAGE_QUESTION_ID = {
  WAKE_COUNTY_GATE: 'PW-FOOD-triage-51',
  WAKE_PM_SLACK_CONFIRM: 'PW-FOOD-triage-52',
  MEMBER_RECEIVED: 'PW-FOOD-triage-03',
  RECEIVED_DAMAGE_TYPE: 'PW-FOOD-triage-05b',
  PERCENT_IMPACTED: 'PW-FOOD-triage-34',
  IS_MF_ORDER: 'PW-FOOD-triage-55a',
  WILL_REPLACE_ORDER: 'PW-FOOD-triage-37',
  BONUS_PACK: 'PW-FOOD-triage-08',
  SEVEN_DAY_OVERRIDE: 'PW-FOOD-triage-35',
  VENDOR_REFUND: 'PW-FOOD-triage-38',
  DAMAGED_ITEMS_NOTE: 'PW-FOOD-triage-20',
  RIGHT_LOCATION: 'PW-FOOD-triage-27',
  BOX_UNATTENDED: 'PW-FOOD-triage-28',
  POST_DISCHARGE_OR_EMERGENCY: 'PW-FOOD-triage-07',
  INSTACART_REPLACEMENT: 'PW-FOOD-triage-16',
  NOT_RECEIVED_REASON: 'PW-FOOD-triage-19',
  INTERNAL_OR_VENDOR_ERROR: 'PW-FOOD-triage-66a',
  LINK_ANOTHER_ORDER: 'PW-FOOD-triage-62',
  PAST_ORDERS_IN_DROPDOWN: 'PW-FOOD-triage-63',
  LINKED_ORDER_IDS: 'PW-FOOD-triage-64',
  ADDITIONAL_FEEDBACK: 'PW-FOOD-triage-50',
  BOX_UNATTENDED_DETAIL: 'PW-FOOD-triage-29',
  /** Alternate / legacy flow sections (not wired in main modal `index` yet). */
  WRONG_ADDRESS_GATE: 'PW-FOOD-triage-53',
  ERROR_ATTRIBUTION: 'PW-FOOD-triage-54',
  DELIVERY_ISSUE_GENERAL: 'PW-FOOD-triage-55',
  MEMBER_RECEIVED_ALT: 'PW-FOOD-triage-56',
  DELIVERY_ISSUE_SPECIFIC: 'PW-FOOD-triage-57',
  PERCENT_IMPACTED_ALT: 'PW-FOOD-triage-58',
  MF_ORDER_PLACE: 'PW-FOOD-triage-59',
  PROVIDE_BONUS_PACK_ALT: 'PW-FOOD-triage-60',
  REQUEST_BONUS_PACK_ALT: 'PW-FOOD-triage-61',
  LINK_ANOTHER_ORDER_ALT: 'PW-FOOD-triage-66',
} as const;

export type TriageSurveyQuestionId = (typeof TRIAGE_QUESTION_ID)[keyof typeof TRIAGE_QUESTION_ID];

/**
 * Order used to trim dependent answers when an earlier question changes (see `handleQuestion` in `index.tsx`).
 */
export const TRIAGE_SURVEY_QUESTION_ORDER: readonly TriageSurveyQuestionId[] = [
  TRIAGE_QUESTION_ID.WAKE_COUNTY_GATE,
  TRIAGE_QUESTION_ID.WAKE_PM_SLACK_CONFIRM,
  TRIAGE_QUESTION_ID.MEMBER_RECEIVED,
  TRIAGE_QUESTION_ID.NOT_RECEIVED_REASON,
  TRIAGE_QUESTION_ID.INTERNAL_OR_VENDOR_ERROR,
  TRIAGE_QUESTION_ID.RECEIVED_DAMAGE_TYPE,
  TRIAGE_QUESTION_ID.PERCENT_IMPACTED,
  TRIAGE_QUESTION_ID.IS_MF_ORDER,
  TRIAGE_QUESTION_ID.WILL_REPLACE_ORDER,
  TRIAGE_QUESTION_ID.BONUS_PACK,
  TRIAGE_QUESTION_ID.SEVEN_DAY_OVERRIDE,
  TRIAGE_QUESTION_ID.VENDOR_REFUND,
  TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE,
  TRIAGE_QUESTION_ID.RIGHT_LOCATION,
  TRIAGE_QUESTION_ID.BOX_UNATTENDED,
  TRIAGE_QUESTION_ID.POST_DISCHARGE_OR_EMERGENCY,
  TRIAGE_QUESTION_ID.INSTACART_REPLACEMENT,
  TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER,
  TRIAGE_QUESTION_ID.PAST_ORDERS_IN_DROPDOWN,
  TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK,
  TRIAGE_QUESTION_ID.BOX_UNATTENDED_DETAIL,
];

/** Triage tasks are always routed to the RD pool (matches legacy v1 default for most paths). */
export const FOOD_ORDER_TRIAGE_ASSIGN_TO = 'RD Pool';

export const ANSWER_IDS = {
  // Reused from v1
  Yes: 'PW-YES',
  No: 'PW-NO',
  'Less than 50%': 'PW-LESS_THAN_HALF_DAMAGED',
  'More than 50%': 'PW-MORE_THAN_HALF_DAMAGED',
  'Greater than 50%': 'PW-MORE_THAN_HALF_DAMAGED',
  Feedback: 'PW-FEEDBACK',

  // Damage/issue types (member received)
  // Damage/issue types (member received) - reused from v1
  'Damaged, broken, split packaging': 'PW-DAMAGED_BROKEN_SPLIT',
  'Items were thawed and spoiled': 'PW-ITEMS_THAWED_SPOILED',
  "Items were the wrong temperature (i.e frozen when they aren't frozen goods or warm to the touch)":
    'PW-WRONG_TEMPERATURE',

  // Percentage
  // Percentage - reused from v1
  'More than 50% of the delivery': 'PW-MORE_THAN_HALF_DAMAGED',
  'Less than 50% of the delivery': 'PW-LESS_THAN_HALF_DAMAGED',

  // Location - options for 27
  // Location - v2 options for 27
  "Yes - delivered to member's home": 'PW-YES',
  'No - delivered next door, down the street, etc.': 'PW-NO',

  // 65 - Member did not receive
  'Delivered to wrong address (only select this option after confirming address we have on file with member and ensuring it aligns but tracking shows delivered elsewhere)':
    'PW-DELIVERED_WRONG_ADDRESS',
  'NourishedRx error - i.e member should have been paused, request not followed when provided in appropriate time window, address change and failed to update CB':
    'PW-NOURISHEDRX_ERROR',
  'Lost in transit - i.e no tracking confirmation': 'PW-LOST_IN_TRANSIT',
  'No label created or issue with courier - i.e shipment never successfully sent': 'PW-NO_LABEL_COURIER_ISSUE',
  'Package was stolen - i.e tracking shows delivered but member does not have it in their possession':
    'PW-PACKAGE_STOLEN',
  'Member issue - moved and did not notify us of new address or other location change issues': 'PW-MEMBER_ISSUE_MOVED',

  // 66a
  'NourishedRx - internal error': 'PW-NOURISHEDRX_INTERNAL_ERROR',
  'Vendor/Courier - external error': 'PW-VENDOR_COURIER_EXTERNAL_ERROR',

  // Text inputs
  'Damaged items': 'PW-DAMAGED_ITEMS',
  'Box unattended elaboration': 'PW-BOX_UNATTENDED_ELABORATION',
  'Linked order IDs': 'PW-LINKED_ORDER',
} as const;

const SPOILED_MISSING_OPTION =
  'Spoiled/missing items - delivered but items were thawed and spoiled OR items were missing';

export const QUESTIONS = {
  [TRIAGE_QUESTION_ID.WAKE_COUNTY_GATE]: {
    questionText: 'Is this a Wake County Triage?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.WAKE_PM_SLACK_CONFIRM]: {
    questionText:
      'Did you confirm with the PM via a slack message in the WC clinical channel to continue with this triage?',
    options: ['Yes', 'No'],
  },

  [TRIAGE_QUESTION_ID.MEMBER_RECEIVED]: {
    questionText: 'Did the member receive the delivery?',
    options: ['Yes', 'No'],
  },

  [TRIAGE_QUESTION_ID.RECEIVED_DAMAGE_TYPE]: {
    questionText: 'What best explains the issue with the delivery?',
    options: [
      'Damaged, broken, split packaging',
      'Items were thawed and spoiled',
      "Items were the wrong temperature (i.e frozen when they aren't frozen goods or warm to the touch)",
    ],
  },
  [TRIAGE_QUESTION_ID.PERCENT_IMPACTED]: {
    questionText: 'What percentage of the order was impacted?',
    options: ['More than 50% of the delivery', 'Less than 50% of the delivery'],
  },
  [TRIAGE_QUESTION_ID.IS_MF_ORDER]: {
    questionText: 'Is this a MF order?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.WILL_REPLACE_ORDER]: {
    questionText: 'Will NourishedRx be replacing this order?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.BONUS_PACK]: {
    questionText: 'Will NourishedRx be providing a bonus pack?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.SEVEN_DAY_OVERRIDE]: {
    questionText:
      'Is this outside of the 7-day food replacement policy and require an override to create a triage task? This should only apply shipment exceptions, lost in transit, NRx error (i.e address never updated, orders never paused, triage never processed when reported initially on time).',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.VENDOR_REFUND]: {
    questionText: 'Will NourishedRx be requesting a refund for the items?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.DAMAGED_ITEMS_NOTE]: {
    questionText: 'Please record damaged items',
    options: [],
  },

  [TRIAGE_QUESTION_ID.RIGHT_LOCATION]: {
    questionText: 'Did the box get delivered to the right location?',
    options: ["Yes - delivered to member's home", 'No - delivered next door, down the street, etc.'],
  },
  [TRIAGE_QUESTION_ID.BOX_UNATTENDED]: {
    questionText:
      "Was the box sitting unattended for a while? i.e left in the mailroom, at a door the member doesn't check, etc.",
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.POST_DISCHARGE_OR_EMERGENCY]: {
    questionText: 'Is this reship for a Post Discharge or Emergency food delivery?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.INSTACART_REPLACEMENT]: {
    questionText: 'Is the replacement going to be fulfilled with instacart?',
    options: ['Yes', 'No'],
  },

  [TRIAGE_QUESTION_ID.NOT_RECEIVED_REASON]: {
    questionText: 'What best explains the issue with the delivery?',
    options: [
      'Delivered to wrong address (only select this option after confirming address we have on file with member and ensuring it aligns but tracking shows delivered elsewhere)',
      'NourishedRx error - i.e member should have been paused, request not followed when provided in appropriate time window, address change and failed to update CB',
      'Lost in transit - i.e no tracking confirmation',
      'No label created or issue with courier - i.e shipment never successfully sent',
      'Package was stolen - i.e tracking shows delivered but member does not have it in their possession',
      'Member issue - moved and did not notify us of new address or other location change issues',
    ],
  },
  [TRIAGE_QUESTION_ID.INTERNAL_OR_VENDOR_ERROR]: {
    questionText: 'Was this a NourishedRx internal error or vendor/courier error?',
    options: ['NourishedRx - internal error', 'Vendor/Courier - external error'],
  },

  [TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER]: {
    questionText:
      'Is there another food order ID to link to this same triage? i.e a cold pack + produce box both ruined in 1 delivery from MF',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.PAST_ORDERS_IN_DROPDOWN]: {
    questionText: 'Include list of past order IDs in the drop down from last 14 days?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.LINKED_ORDER_IDS]: {
    questionText: 'Please select the order IDs you want to include:',
    options: [],
  },
  [TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK]: {
    questionText: 'Is there anything else you think we should know about this triage?',
    options: [],
  },

  [TRIAGE_QUESTION_ID.BOX_UNATTENDED_DETAIL]: {
    questionText: 'Please elaborate (i.e sitting outside at front porch, left in mailroom, etc):',
    options: [],
  },

  // Alternate-flow sections (parity with `received` / `notReceived` copy where applicable)
  [TRIAGE_QUESTION_ID.WRONG_ADDRESS_GATE]: {
    questionText: 'Was this a delivery to the wrong address?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.ERROR_ATTRIBUTION]: {
    questionText: 'Was this a NourishedRx internal error or vendor/courier error?',
    options: ['NourishedRx - internal error', 'Vendor/Courier - external error'],
  },
  [TRIAGE_QUESTION_ID.DELIVERY_ISSUE_GENERAL]: {
    questionText: 'What best explains the issue with the delivery?',
    options: [
      'Damaged, broken, split packaging',
      'Items were thawed and spoiled',
      "Items were the wrong temperature (i.e frozen when they aren't frozen goods or warm to the touch)",
      'Lost in transit - i.e no tracking confirmation',
      'No label created or issue with courier - i.e shipment never successfully sent',
    ],
  },
  [TRIAGE_QUESTION_ID.MEMBER_RECEIVED_ALT]: {
    questionText: 'Did the member receive the delivery?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.DELIVERY_ISSUE_SPECIFIC]: {
    questionText: 'What best explains the issue with the delivery?',
    options: [
      'Damaged, broken, split packaging',
      'Items were thawed and spoiled',
      "Items were the wrong temperature (i.e frozen when they aren't frozen goods or warm to the touch)",
      SPOILED_MISSING_OPTION,
    ],
  },
  [TRIAGE_QUESTION_ID.PERCENT_IMPACTED_ALT]: {
    questionText: 'What percentage of the order was impacted?',
    options: ['More than 50%', 'Less than 50%'],
  },
  [TRIAGE_QUESTION_ID.MF_ORDER_PLACE]: {
    questionText: 'Will NourishedRx be placing an MF order for this replacement?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.PROVIDE_BONUS_PACK_ALT]: {
    questionText: 'Will NourishedRx be providing a bonus pack?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.REQUEST_BONUS_PACK_ALT]: {
    questionText: 'Should we request a bonus pack for the member?',
    options: ['Yes', 'No'],
  },
  [TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER_ALT]: {
    questionText:
      'Is there another food order ID to link to this same triage? i.e a cold pack + produce box both ruined in 1 delivery from MF',
    options: ['Yes', 'No'],
  },
} as const;
